import React from "react";
import { Card, Typography, Tag, Space, Divider, Avatar } from "antd";
import { ClockCircleOutlined, ArrowRightOutlined, UserOutlined, MessageOutlined, WarningOutlined, CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
    SourceType,
    ActionType,
    ActionItemStatus,
    ActionItemResolution,
    SOURCE_TYPE_LABELS,
    ACTION_TYPE_LABELS,
    PROCESSING_STAGE_INFO,
    ACTION_ITEM_STATUS_INFO,
    ACTION_ITEM_RESOLUTION_INFO,
    PRIORITY_INFO
} from "../../features/workspace/NotificationCenter/constants/actionItemConstants";

const { Text, Title } = Typography;

/**
 * V3.0 生成标题信息
 * 优先使用 context_data_summary，回退到旧逻辑
 */
const generateTitleInfo = (actionItem) => {
    const { suggested_payload, source_type } = actionItem;
    const { context_data_summary, context_summary, system_operations = [] } = suggested_payload || {};

    // 特殊处理合同到期预警
    if (source_type === "CONTRACT_EXPIRY_WARNING") {
        // 从additional_info中获取合同信息
        const contractInfo = actionItem.additional_info?.contract_info;
        if (contractInfo) {
            return {
                flightNumber: contractInfo.contract_name || "合同预警",
                flightRoute: `编号: ${contractInfo.contract_number || "未知"}`,
                flightDate: contractInfo.expiry_date ? dayjs(contractInfo.expiry_date).format("DD-MMM") : ""
            };
        }
        return { flightNumber: "合同到期预警", flightRoute: "", flightDate: "" };
    }

    // 优先使用 context_summary 中的航班信息 (新结构)
    if (context_summary?.flight_summary) {
        const { flight_number, departure_airport, arrival_airport } = context_summary.flight_summary;
        const flight_date = context_summary.flight_date;

        let title = flight_number || "未知航班";
        let route = "";
        let date = "";

        if (departure_airport && arrival_airport) {
            route = `${departure_airport}-${arrival_airport}`;
        }

        if (flight_date) {
            date = dayjs.utc(flight_date).format("DD-MMM");
        }

        return { flightNumber: title, flightRoute: route, flightDate: date };
    }

    // 回退使用 context_data_summary 中的航班信息 (旧结构)
    if (context_data_summary?.flight_details) {
        const { flight_number, departure_airport, arrival_airport, flight_date_utc } = context_data_summary.flight_details;

        let title = flight_number || "未知航班";
        let route = "";
        let date = "";

        if (departure_airport && arrival_airport) {
            route = `${departure_airport}-${arrival_airport}`;
        }

        if (flight_date_utc) {
            date = dayjs.utc(flight_date_utc).format("DD-MMM");
        }

        return { flightNumber: title, flightRoute: route, flightDate: date };
    }

    // 回退到旧逻辑
    return { flightNumber: "未知航班", flightRoute: "", flightDate: "" };
};

/**
 * V3.0 生成摘要信息
 * 基于 system_operations 或回退到旧逻辑
 */
const generateSummaryInfo = (actionItem) => {
    const { suggested_payload, source_type, additional_info } = actionItem;
    const { context_data_summary, context_summary, system_operations = [] } = suggested_payload || {};

    // 特殊处理合同到期预警
    if (source_type === SourceType.CONTRACT_EXPIRY_WARNING && additional_info?.contract_info) {
        const { contract_name, contract_number, expiry_date, days_until_expiry } = additional_info.contract_info;
        const expiryDateFormatted = dayjs(expiry_date).format("YYYY-MM-DD");
        
        if (days_until_expiry <= 0) {
            return `合同已到期 (${expiryDateFormatted})`;
        } else if (days_until_expiry <= 7) {
            return `合同将在 ${days_until_expiry} 天后到期 (${expiryDateFormatted})`;
        } else if (days_until_expiry <= 30) {
            return `合同将在 ${days_until_expiry} 天后到期 (${expiryDateFormatted})`;
        } else {
            return `合同到期提醒 - ${days_until_expiry} 天后到期 (${expiryDateFormatted})`;
        }
    }

    // 优先使用 context_data_summary 中的摘要
    if (context_data_summary?.summary) {
        return context_data_summary.summary;
    }

    // 基于 system_operations 生成摘要 - 修复字段名问题
    if (system_operations.length > 0) {
        const operation = system_operations[0];
        
        // 检查 change_type (新结构) 或 operation_type (旧结构)
        const operationType = operation.parameters?.change_type || operation.change_type || operation.operation_type;
        const serviceType = operation.service_name || operation.target_service;
        
        if (operationType) {
            if (system_operations.length === 1) {
                // 特殊处理 MVT 相关操作
                if (operationType.includes("UPDATE_OPERATION_FLIGHT_FROM_MVT")) {
                    // 从 parameters.updates 中提取 MVT 详细信息
                    const updates = operation.parameters?.updates;
                    if (updates) {
                        let summary = "";
                        
                        // 处理起飞信息
                        if (updates.new_atd_utc) {
                            summary = `Off-block: ${dayjs.utc(updates.new_atd_utc).format("HH:mm")}Z`;
                            if (updates.new_atd_icao_utc) {
                                summary += ` | Airborne: ${dayjs.utc(updates.new_atd_icao_utc).format("HH:mm")}Z`;
                            }
                        }
                        
                        // 处理到达信息
                        if (updates.new_ata_utc) {
                            if (summary) summary += " | ";
                            summary += `On-block: ${dayjs.utc(updates.new_ata_utc).format("HH:mm")}Z`;
                            if (updates.new_ata_icao_utc) {
                                summary += ` | Touch-down: ${dayjs.utc(updates.new_ata_icao_utc).format("HH:mm")}Z`;
                            }
                        }
                        
                        // 处理预计时间
                        if (!summary) {
                            if (updates.new_etd_utc) {
                                summary = `ETD: ${dayjs.utc(updates.new_etd_utc).format("HH:mm")}Z`;
                            } else if (updates.new_eta_utc) {
                                summary = `ETA: ${dayjs.utc(updates.new_eta_utc).format("HH:mm")}Z`;
                            }
                        }
                        
                        // 添加延误信息
                        if (updates.delay_code) {
                            summary += ` | 延误代码: ${updates.delay_code}`;
                        }
                        if (updates.delay_minutes) {
                            summary += ` (${updates.delay_minutes}分钟)`;
                        }
                        
                        if (summary) {
                            // 根据 context_summary 中的 mvt_message_type 添加前缀
                            const mvtType = context_summary?.mvt_message_type || "MVT";
                            return `${mvtType}: ${summary}`;
                        }
                    }
                    
                    // 如果没有具体更新信息，显示基本的MVT信息
                    const mvtType = context_summary?.mvt_message_type || "MVT";
                    return `航班时刻更新 (${mvtType})`;
                }
                
                // 其他操作类型的处理
                switch (operationType) {
                    case 'CREATE_RECORD':
                        if (serviceType === 'CateringOrderService') {
                            return '创建航食订单';
                        }
                        return `创建记录 (${serviceType || '未知服务'})`;
                    case 'UPDATE_RECORD':
                        return `更新记录 (${serviceType || '未知服务'})`;
                    case 'REPLY_EMAIL':
                        return `回复邮件: ${operation.parameters?.subject || ''}`;
                    case 'SEND_EMAIL':
                        return `发送邮件: ${operation.parameters?.subject || ''}`;
                    default:
                        return operationType;
                }
            } else {
                return `执行 ${system_operations.length} 个操作`;
            }
        }
    }

    // 回退到旧的 MVT 逻辑
    const currentActionType = suggested_payload?.action_type || actionItem.suggested_change_type;
    if (currentActionType && currentActionType.includes("UPDATE_OPERATION_FLIGHT_FROM_MVT") && suggested_payload?.action_parameters) {
        const params = suggested_payload.action_parameters;

        if (params.new_atd_utc) {
            let summary = `Off-block: ${dayjs.utc(params.new_atd_utc).format("HH:mm")}Z`;
            if (params.new_atd_icao_utc) {
                summary += ` | Airborne: ${dayjs.utc(params.new_atd_icao_utc).format("HH:mm")}Z`;
            }
            if (params.delay_code) {
                summary += ` | 延误代码: ${params.delay_code}`;
            }
            const delayMinutes = params.delay_minutes || params.mvt_full_data?.delay_duration;
            if (delayMinutes) {
                summary += ` (${delayMinutes}分钟)`;
            }
            return summary;
        } else if (params.new_ata_utc) {
            let summary = `On-block: ${dayjs.utc(params.new_ata_utc).format("HH:mm")}Z`;
            if (params.new_ata_icao_utc) {
                summary += ` | Touch-down: ${dayjs.utc(params.new_ata_icao_utc).format("HH:mm")}Z`;
            }
            return summary;
        } else if (params.new_etd_utc) {
            return `ETD: ${dayjs.utc(params.new_etd_utc).format("HH:mm")}Z`;
        } else if (params.new_eta_utc) {
            return `ETA: ${dayjs.utc(params.new_eta_utc).format("HH:mm")}Z`;
        }
    }

    return "待处理";
};

/**
 * V3.0 更新：获取行动项的状态标签和颜色
 * 优先使用新的状态字段，回退到旧字段
 */
const getStageInfo = (actionItem) => {
    // 优先使用新的 status 字段
    if (actionItem.status && ACTION_ITEM_STATUS_INFO[actionItem.status]) {
        return ACTION_ITEM_STATUS_INFO[actionItem.status];
    }

    // 回退使用 resolution 字段
    if (actionItem.resolution && ACTION_ITEM_RESOLUTION_INFO[actionItem.resolution]) {
        return ACTION_ITEM_RESOLUTION_INFO[actionItem.resolution];
    }

    // 兼容旧字段
    if (actionItem.processing_stage && PROCESSING_STAGE_INFO[actionItem.processing_stage]) {
        return PROCESSING_STAGE_INFO[actionItem.processing_stage];
    }

    // 最终回退
    return { label: actionItem.status || actionItem.processing_stage || "未知状态", color: "default" };
};

/**
 * 行动项卡片组件
 * 用于在列表中展示单个行动项的摘要信息
 *
 * @param {Object} props
 * @param {Object} props.actionItem - 行动项数据
 * @param {Function} props.onClick - 点击卡片的回调函数
 * @param {boolean} props.selected - 是否被选中
 */
const NotificationCard = ({ actionItem, onClick, selected }) => {
    // V3.0 更新：优先使用 context_data_summary 生成标题和摘要
    const { suggested_payload } = actionItem;
    const { context_data_summary, system_operations = [], user_interaction = {} } = suggested_payload || {};

    // 生成标题信息
    let titleInfo = generateTitleInfo(actionItem);

    // 生成摘要信息
    let summaryInfo = generateSummaryInfo(actionItem);

    const stageInfo = getStageInfo(actionItem);
    const priorityInfo = PRIORITY_INFO[actionItem.priority] || { label: "未知", color: "default" };

    const mvtMessageType = actionItem.suggested_payload?.context_data_summary?.mvt_message_type;

    return (
        <Card
            hoverable
            onClick={() => onClick(actionItem)}
            style={{
                marginBottom: "12px",
                borderLeft: selected ? "4px solid #1677ff" : "none",
                backgroundColor: selected ? "#f0f8ff" : "white",
            }}
            size="small"
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Space direction="vertical" size={4} style={{ flex: 1 }}>
                    <div>
                        <Title level={5} style={{ margin: 0 }}>
                            {titleInfo.flightNumber}
                            <Divider type="vertical" style={{ margin: "0 4px" }} />
                            {titleInfo.flightDate && <Text type="secondary">{titleInfo.flightDate}</Text>}
                            <Divider type="vertical" style={{ margin: "0 4px" }} />
                            {titleInfo.flightRoute && (
                                <Text type="secondary">
                                    <ArrowRightOutlined /> {titleInfo.flightRoute}
                                </Text>
                            )}
                        </Title>
                        {actionItem.priority && <Tag color={priorityInfo.color}>优先级: {priorityInfo.label}</Tag>}
                    </div>

                    <div>
                        <Text strong>{SOURCE_TYPE_LABELS[actionItem.source_type] || actionItem.source_type}: </Text>
                        {/* V3.0 更新：显示操作类型或系统操作摘要 */}
                        {(() => {
                            const currentActionType = actionItem.suggested_payload?.action_type || actionItem.suggested_change_type;
                            const systemOperations = actionItem.suggested_payload?.system_operations;

                            if (systemOperations && systemOperations.length > 0) {
                                const operation = systemOperations[0];
                                // 检查 change_type (新结构) 或 operation_type (旧结构)
                                const operationType = operation.parameters?.change_type || operation.change_type || operation.operation_type;
                                
                                if (systemOperations.length === 1 && operationType) {
                                    // 特殊处理 MVT 操作显示
                                    if (operationType.includes("UPDATE_OPERATION_FLIGHT_FROM_MVT")) {
                                        const mvtType = actionItem.suggested_payload?.context_summary?.mvt_message_type || "MVT";
                                        return <Text>航班时刻更新 ({mvtType})</Text>;
                                    }
                                    return <Text>{operationType}</Text>;
                                } else if (systemOperations.length > 1) {
                                    return <Text>{systemOperations.length} 个操作</Text>;
                                } else {
                                    return <Text>系统操作</Text>;
                                }
                            } else if (currentActionType) {
                                return <Text>{ACTION_TYPE_LABELS[currentActionType] || currentActionType}</Text>;
                            } else {
                                return <Text>未知操作</Text>;
                            }
                        })()}
                        {actionItem.source_type === SourceType.FLIGHT_MESSAGE_MVT && mvtMessageType && (
                            <Text type="secondary" style={{ marginLeft: "8px" }}>
                                {mvtMessageType}
                            </Text>
                        )}
                    </div>

                    {summaryInfo && <Text type="secondary">{summaryInfo}</Text>}
                </Space>

                <Space direction="vertical" size={24} style={{ alignItems: "flex-end" }}>
                    <Tag color={stageInfo.color} style={{ marginRight: 0 }}>
                        {stageInfo.label}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        <ClockCircleOutlined /> {dayjs(actionItem.created_at).format("MM-DD HH:mm")}
                    </Text>
                </Space>
            </div>
        </Card>
    );
};

export default NotificationCard;
